/**
 * LocationPanel — Floating side panel for the PalPendant Locator page.
 *
 * Replaces V1's fixed blue sidebar with a collapsible, card-style floating panel.
 * Features: search, ping, tracking mode (long-press), campus selector, alert indicators.
 *
 * Props:
 *   locations          — array of location objects
 *   alertedDevices     — array of alerted device objects
 *   onLocationSelect   — (location) => void — fly to + select marker
 *   campusId           — current campus ID
 *   onCampusChange     — (newCampusId) => void
 *   campusList         — array of { campus_id, campus_name }
 *   showCampusSelector — boolean (true for role_weight >= 30)
 *   onPingDevice       — (manufacture_uuid) => void
 *   pingingDevice      — manufacture_uuid currently being pinged (null when idle)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import InfoPopover from '../../../../components/v2/InfoPopover.jsx';

const LocationPanel = ({
    locations,
    alertedDevices,
    onLocationSelect,
    campusId,
    onCampusChange,
    campusList,
    showCampusSelector,
    onPingDevice,
    pingingDevice,
}) => {
    // -- Panel state --
    const [collapsed, setCollapsed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // -- Tracking state --
    const [trackingMode, setTrackingMode] = useState(false);
    const [trackedDevices, setTrackedDevices] = useState(new Set());
    const longPressTimerRef = useRef(null);
    const trackedCoordsRef = useRef(new Map());

    // -- Tracking info popover --

    // ======================================================================
    // Search filter
    // ======================================================================
    const filteredLocations = (locations || []).filter((loc) =>
        loc.resident_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.manufacture_uuid?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ======================================================================
    // Location click → fly to + select marker
    // ======================================================================
    const handleLocationClick = (location) => {
        onLocationSelect(location);
    };

    // ======================================================================
    // Ping click
    // ======================================================================
    const handlePingClick = (e, location) => {
        e.stopPropagation();
        onPingDevice(location.manufacture_uuid);
    };

    // ======================================================================
    // Tracking: long-press handlers
    // ======================================================================
    const handlePressStart = (location) => {
        if (!trackingMode) return;

        longPressTimerRef.current = setTimeout(() => {
            toggleTracking(location.manufacture_uuid);
        }, 1000);
    };

    const handlePressEnd = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const toggleTracking = useCallback((uuid) => {
        setTrackedDevices((prev) => {
            const next = new Set(prev);
            if (next.has(uuid)) {
                next.delete(uuid);
                trackedCoordsRef.current.delete(uuid);
            } else {
                next.add(uuid);
                // Store initial coords
                const loc = (locations || []).find((l) => l.manufacture_uuid === uuid);
                if (loc) {
                    trackedCoordsRef.current.set(uuid, {
                        lat: loc.latitude,
                        lng: loc.longitude,
                    });
                }
            }
            return next;
        });
    }, [locations]);

    // Clear tracked devices when tracking mode is turned off
    useEffect(() => {
        if (!trackingMode) {
            setTrackedDevices(new Set());
            trackedCoordsRef.current.clear();
        }
    }, [trackingMode]);

    // ======================================================================
    // Auto-flyTo when tracked device coordinates change
    // ======================================================================
    useEffect(() => {
        if (!trackingMode || trackedDevices.size === 0) return;

        let changedLocation = null;

        (locations || []).forEach((loc) => {
            if (trackedDevices.has(loc.manufacture_uuid)) {
                const oldCoords = trackedCoordsRef.current.get(loc.manufacture_uuid);

                if (oldCoords && (oldCoords.lat !== loc.latitude || oldCoords.lng !== loc.longitude)) {
                    changedLocation = loc;
                }

                // Update stored coords
                trackedCoordsRef.current.set(loc.manufacture_uuid, {
                    lat: loc.latitude,
                    lng: loc.longitude,
                });
            }
        });

        // Fly to the first tracked device that moved
        if (changedLocation) {
            onLocationSelect(changedLocation);
        }
    }, [locations, trackedDevices, trackingMode, onLocationSelect]);

    // ======================================================================
    // Campus change
    // ======================================================================
    const handleCampusChange = (e) => {
        onCampusChange(parseInt(e.target.value, 10));
    };

    // ======================================================================
    // Alert check helper
    // ======================================================================
    const isAlerted = (uuid) =>
        (alertedDevices || []).some((d) => d.manufacture_uuid === uuid);

    // ======================================================================
    // Render — collapsed
    // ======================================================================
    if (collapsed) {
        return (
            <div className="pm-panel pm-panel--collapsed">
                <div className="pm-panel__header">
                    <button
                        className="pm-panel__toggle"
                        onClick={() => setCollapsed(false)}
                        title="Expand panel"
                    >
                        <i className="fe fe-chevrons-left" />
                    </button>
                </div>
            </div>
        );
    }

    // ======================================================================
    // Render — expanded
    // ======================================================================
    return (
        <div className="pm-panel">
            {/* Header */}
            <div className="pm-panel__header">
                <h3 className="pm-panel__title">PalPendant Locator</h3>
                <button
                    className="pm-panel__toggle"
                    onClick={() => setCollapsed(true)}
                    title="Collapse panel"
                >
                    <i className="fe fe-chevrons-right" />
                </button>
            </div>

            {/* Body */}
            <div className="pm-panel__body">
                {/* Campus selector */}
                {showCampusSelector && campusList && campusList.length > 1 ? (
                    <div className="pm-campus-row">
                        <select
                            className="pm-campus-select"
                            value={campusId}
                            onChange={handleCampusChange}
                        >
                            {campusList.map((c) => (
                                <option key={c.campus_id} value={c.campus_id}>
                                    {c.campus_name}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}

                {/* Search */}
                <input
                    className="pm-panel__search"
                    type="text"
                    placeholder="Search by name or pendant #..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                {/* Location list */}
                <div className="pm-location-list">
                    {filteredLocations.length === 0 ? (
                        <p className="pm-location-empty">No devices found.</p>
                    ) : null}

                    {filteredLocations.map((location) => {
                        const alerted = isAlerted(location.manufacture_uuid);
                        const tracked = trackedDevices.has(location.manufacture_uuid);

                        return (
                            <div
                                key={location.manufacture_uuid}
                                className={
                                    `pm-location` +
                                    (tracked ? ' pm-location--tracked' : '') +
                                    (alerted ? ' pm-location--alerted' : '')
                                }
                                onClick={() => handleLocationClick(location)}
                                onMouseDown={() => handlePressStart(location)}
                                onMouseUp={handlePressEnd}
                                onMouseLeave={handlePressEnd}
                                onTouchStart={() => handlePressStart(location)}
                                onTouchEnd={handlePressEnd}
                            >
                                <div className="pm-location__info">
                                    <div className="pm-location__name">
                                        {location.resident_name}
                                        {alerted ? (
                                            <span className="pm-location__alert-badge">!</span>
                                        ) : null}
                                    </div>
                                    <div className="pm-location__device">
                                        {location.manufacture_uuid}
                                    </div>
                                </div>

                                <div className="pm-location__ping">
                                    <button
                                        className="dp-btn dp-btn--sm dp-btn--secondary"
                                        onClick={(e) => handlePingClick(e, location)}
                                        disabled={pingingDevice === location.manufacture_uuid}
                                    >
                                        {pingingDevice === location.manufacture_uuid
                                            ? 'Pinging...'
                                            : 'Ping'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Footer — tracking controls */}
            <div className="pm-panel__footer">
                <div className="pm-tracking-counter">
                    Tracking: {trackedDevices.size} device{trackedDevices.size !== 1 ? 's' : ''}
                </div>
                <div className="pm-tracking-toggle">
                    <label className="dp-toggle">
                        <input
                            type="checkbox"
                            checked={trackingMode}
                            onChange={(e) => setTrackingMode(e.target.checked)}
                        />
                        <span className="dp-toggle__track" />
                    </label>
                    <span className="pm-tracking-label">Tracking Mode</span>
                    <InfoPopover
                        heading="Tracking Mode"
                        label="About Tracking Mode"
                        sections={[
                            { body: 'When enabled, the map will automatically move to each actively tracked pendant as its information updates.' },
                            { body: 'To activate tracking for a device, select and hold (long-press) on it in the list above.' },
                        ]}
                        marker="none"
                        maxWidth={280}
                        flipBelow={200}
                        triggerClassName="pm-tracking-info-btn"
                    >
                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                    </InfoPopover>
                </div>
            </div>
        </div>
    );
};

export default LocationPanel;
